import * as React from 'react';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import { IValuesCollection } from '../../ts/appTheme';
import { IDataQueryData, IGlobalFilterProperties, IQueryFilter } from '../filtering/Interfaces';
import { AttributeValueSortingType } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { GetValuesResult } from '../SuggestionListField';
import { getEntityValuesFilteredByGlobalFilter } from '../filtering/AttributeFiltersCreator';
import { IParameterFieldBaseOptions } from '../../ts/ParameterFieldBase';

export interface IGetEntityValuesParameters<TOptions extends IParameterFieldBaseOptions = IParameterFieldBaseOptions> {
    dataSourceID: number;
    attributeData: IDataQueryData;
    globalFilter?: IGlobalFilterProperties;
    localFilter?: IQueryFilter[];
    sortingType?: AttributeValueSortingType;
    options?: TOptions;
    theme?: IValuesCollection;
    glob: IGlob;
}

export function useGetEntityValuesHook(params: IGetEntityValuesParameters) {
    const [ignoreBackendCache, setIgnoreBackendCache] = React.useState(false);
    const loadedValuesKeys = React.useRef<{ [valuesType: string]: string }>({});

    React.useEffect(() => {
        const handleOnCacheClearedChange = () => {
            setIgnoreBackendCache(true);
        };

        params.glob.EntityModelsStore.addOnCacheClearedListener(handleOnCacheClearedChange);

        return () => {
            params.glob.EntityModelsStore.removeOnCacheClearedListener(handleOnCacheClearedChange);
        };
    }, []);

    const cleanPreviousEntityValuesCache = React.useCallback((valuesType: string, newCollectionKey: string) => {
        const oldCollectionKey = loadedValuesKeys.current[valuesType];

        if (oldCollectionKey !== newCollectionKey) {
            if (oldCollectionKey != null) {
                params.glob.EntityModelsStore.cleanDataCache(oldCollectionKey);
            }

            loadedValuesKeys.current[valuesType] = newCollectionKey;
        }
    }, []);

    return (): GetValuesResult => {
        const theme = params.theme ?? params.options?.theme;

        if (params.globalFilter != null) {
            return getEntityValuesFilteredByGlobalFilter(
                params.dataSourceID,
                params.attributeData,
                params.globalFilter,
                params.localFilter,
                params.sortingType,
                ignoreBackendCache,
                theme,
                params.glob,
                cleanPreviousEntityValuesCache,
                (value: boolean) => {
                    setIgnoreBackendCache(value);
                }
            );
        } else {
            return params.glob.EntityModelsStore.getEntityValues(
                params.dataSourceID,
                params.attributeData,
                theme,
                params.localFilter
            );
        }
    };
}
